import {
  ChangeEvent,
  useId,
  useCallback,
  useMemo,
  type CSSProperties,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
} from 'react';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid2 from '@mui/material/Grid2';
import clsx from 'clsx';
import * as IconMDI from 'mdi-material-ui';
import useStyles from './styles';
import { Avatar, Button, primaryOutlinedButtonSx, type Media } from '@elevator/ui';
import Image from 'next/image';
import { AuthImageSrc } from 'config/staticAssets';

/**
 * ImageUploader Component
 *
 * A versatile image upload component with multiple display variants for different use cases.
 * Supports single file uploads, multiple file selection, avatar uploads, and banner uploads.
 *
 * Variants:
 * - `content`: Standard upload with button (supports `buttonPosition: 'left' | 'top'`)
 * - `avatar`: Circular avatar upload with camera icon overlay and gradient fade
 * - `banner`: Wide banner image upload with gradient overlay and default placeholder
 * - `multiple`: Multiple file selection with preview grid
 *
 * Features:
 * - Supports both File objects and objects with `sourceUrl` property
 * - Automatic preview generation for local (blob/data) and remote URLs
 * - Error state display with helper text
 * - Disabled state support
 * - Required field indicator
 * - Customizable labels and helper text
 * - Accessible file input with proper ARIA attributes
 *
 * @template T - The variant type, determines which props are available
 * @param props - Component props
 * @param props.variant - Display variant ('content' | 'avatar' | 'banner' | 'multiple' | 'singleFile')
 * @param props.error - Whether to display error state
 * @param props.helperText - Helper text displayed below the uploader
 * @param props.inputProps - Props passed to the underlying file input element
 * @param props.file - Single file (File object or object with sourceUrl). Not allowed for 'multiple' variant.
 * @param props.files - Array of files. Only allowed for 'multiple' variant.
 * @param props.onRemoveImage - Callback when image is removed. For 'multiple' variant, receives index.
 * @param props.label - Label text displayed above the uploader (default: 'Image')
 * @param props.avatarName - Initial letter for avatar fallback (default: 'E')
 * @param props.disabled - Whether the uploader is disabled
 * @param props.required - Whether the field is required (shows asterisk)
 * @param props.renderLabel - Whether to render the label (default: true)
 * @param props.buttonPosition - Button position for 'content' variant ('left' | 'top', default: 'top')
 */
const ImageUploader = <T extends ImageUploaderVariant>(
  props: ImageUploaderPropTypes<T>,
) => {
  const {
    error,
    helperText,
    inputProps,
    file,
    files,
    onRemoveImage,
    label = 'Image',
    renderLabel = true,
    avatarName = 'E',
    disabled = false,
    required = false,
    variant,
    buttonPosition,
    hideBannerFooter = false,
  } = props;

  const resolvedVariant: ImageUploaderVariant = variant ?? 'content';
  const resolvedButtonPosition = buttonPosition ?? 'top';
  const { classes } = useStyles();
  const generatedId = useId();

  /**
   * Extracts the file name from various file types.
   * Returns name from File, path from Media/FileWithSourceUrl, or null for Blob.
   */
  const getFileName = useCallback(
    (file?: File | Blob | Media | FileWithSourceUrl | null): string | null => {
      if (!file) return null;

      if (file instanceof File) {
        return file.name;
      }

      if (typeof file === 'object' && 'name' in file && file.name) {
        return file.name;
      }

      if (typeof file === 'object' && 'path' in file && file.path) {
        return file.path;
      }

      return null;
    },
    [],
  );

  /**
   * Extracts the image URL from a file object.
   * Supports File/Blob objects (creates object URL) and objects with sourceUrl property.
   */
  const getImageURL = useCallback(
    (renderedFile?: File | Blob | Media | FileWithSourceUrl | null): string | null => {
      if (!renderedFile) return null;

      if (renderedFile instanceof Blob || renderedFile instanceof File) {
        return URL.createObjectURL(renderedFile);
      }

      if (typeof renderedFile === 'object' && 'sourceUrl' in renderedFile) {
        return renderedFile.sourceUrl;
      }

      return null;
    },
    [],
  );

  /**
   * Renders a preview image component.
   * Uses native img for local (blob/data) URLs and Next.js Image for remote URLs.
   */
  const renderPreviewImage = useCallback(
    (
      renderedFile?: File | Blob | Media | FileWithSourceUrl | null,
      options: { className?: string; style?: CSSProperties; priority?: boolean } = {},
    ) => {
      const src = getImageURL(renderedFile);
      if (!src) return null;

      const isLocal = src.startsWith('blob:') || src.startsWith('data:');
      const commonProps = {
        className: options.className,
        style: { width: '100%', height: 'auto', ...options.style },
      } as const;

      if (isLocal) {
        return (
          <Box
            component={'img'}
            src={src}
            alt="Image preview"
            aria-label="Image preview"
            {...commonProps}
          />
        );
      }

      return (
        <Image
          src={src}
          alt="Image preview"
          width={800}
          height={600}
          sizes="100vw"
          className={options.className}
          style={commonProps.style}
          priority={options.priority}
        />
      );
    },
    [getImageURL],
  );

  /**
   * Renders the upload button (camera icon) for avatar and banner variants.
   */
  const renderUploadButton = useCallback(
    (iconButtonClassName?: string) => {
      if (disabled) return null;
      return (
        <IconButton
          component={'label'}
          size={'small'}
          className={clsx(classes.iconButton, iconButtonClassName)}
          aria-label="Upload image"
        >
          <IconMDI.Camera />
          <input
            accept="image/*"
            type={'file'}
            className={classes.imageInput}
            aria-label="Upload image"
            {...inputProps}
          />
        </IconButton>
      );
    },
    [disabled, classes, inputProps],
  );

  /**
   * Renders the remove button (close icon) for avatar and banner variants.
   */
  const renderRemoveButton = useCallback(() => {
    if (disabled) return null;
    // Type guard: for non-multiple variants, onRemoveImage is () => void
    const handleRemove = () => {
      if (resolvedVariant !== 'multiple') {
        (onRemoveImage as () => void)();
      }
    };
    return (
      <Tooltip title={'Remove this image'}>
        <IconButton
          size={'small'}
          className={clsx(
            classes.removeButton,
            resolvedVariant === 'banner'
              ? classes.removeButtonBanner
              : classes.removeButtonAvatar,
          )}
          onClick={handleRemove}
          aria-label="Remove image"
        >
          <IconMDI.Close />
        </IconButton>
      </Tooltip>
    );
  }, [disabled, classes, onRemoveImage, resolvedVariant]);

  /**
   * Renders the file input trigger button with hidden input element.
   */
  const renderUploadTrigger = useCallback(
    (
      labelText: string,
      options: {
        multiple?: boolean;
        fullWidth?: boolean;
        size?: 'small' | 'medium' | 'large';
        buttonClassName?: string;
        idSuffix: string;
      },
    ) => {
      const {
        multiple = false,
        fullWidth = true,
        size = 'small',
        buttonClassName,
        idSuffix,
      } = options;
      const { multiple: _ignored, ...restInputProps } = inputProps ?? {};
      const inputId = restInputProps.id ?? `${generatedId}-${idSuffix}`;
      const mergedInputProps = {
        accept: restInputProps.accept ?? 'image/*',
        type: 'file' as const,
        ...restInputProps,
        ...(multiple ? { multiple: true } : {}),
        id: inputId,
        'aria-label': restInputProps['aria-label'] ?? `Upload ${labelText.toLowerCase()}`,
      };

      return (
        <>
          <input className={classes.imageInput} {...mergedInputProps} />
          <label htmlFor={inputId} className={classes.uploadLabel}>
            <Button
              variant={'outlined'}
              sx={primaryOutlinedButtonSx}
              component={'span'}
              fullWidth={fullWidth}
              size={size}
              className={buttonClassName}
              aria-label={mergedInputProps['aria-label']}
            >
              {labelText}
            </Button>
          </label>
        </>
      );
    },
    [generatedId, inputProps, classes],
  );

  /**
   * Renders the avatar variant with circular preview and camera icon overlay.
   */
  const renderAvatarVariant = useCallback(() => {
    return (
      <>
        <Box className={classes.avatarContainer}>
          <Box
            position={'absolute'}
            bottom={0}
            className={`${classes.gradientFade} ${classes.avatarGradient}`}
          />
          {file && (
            <Grow in>
              <div>
                {renderPreviewImage(file, {
                  className: classes.avatar,
                })}
                {renderRemoveButton()}
              </div>
            </Grow>
          )}
          {!file && (
            <Grow in>
              <Box className={classes.avatar}>
                <Avatar username={avatarName} size="large" />
              </Box>
            </Grow>
          )}
          {renderUploadButton()}
        </Box>
        <Box paddingTop={1}>
          <Typography
            color={'textSecondary'}
            variant={'caption'}
            className={clsx(classes.imageUploadsDisclaimer, error && classes.errorText)}
          >
            {helperText}
          </Typography>
        </Box>
      </>
    );
  }, [
    file,
    avatarName,
    classes,
    error,
    helperText,
    renderPreviewImage,
    renderRemoveButton,
    renderUploadButton,
  ]);

  /**
   * Renders the content variant with configurable button position.
   */
  const renderContentVariant = useCallback(
    (buttonPosition: ButtonPositionVariant) => {
      switch (buttonPosition) {
        case 'top':
          return (
            <>
              <Typography variant={'subtitle2'}>
                {renderLabel ? label : ''}
                {required && ' *'}
              </Typography>
              <Grid2 container spacing={1} alignItems={'center'}>
                <Grid2>
                  {renderUploadTrigger('UPLOAD IMAGE', {
                    idSuffix: 'content-top',
                    buttonClassName: classes.uploadButton,
                  })}
                </Grid2>
                {file && (
                  <Grid2 sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                    <Typography variant={'caption'} className={classes.fileName}>
                      {getFileName(file) || 'Image'}
                    </Typography>
                    <Tooltip title={'Remove image'}>
                      <IconButton
                        size={'small'}
                        color={'error'}
                        onClick={() => {
                          if (resolvedVariant !== 'multiple') {
                            (onRemoveImage as () => void)();
                          }
                        }}
                        aria-label="Remove image"
                      >
                        <IconMDI.Close />
                      </IconButton>
                    </Tooltip>
                  </Grid2>
                )}
              </Grid2>

              <Typography
                color={'textSecondary'}
                variant={'caption'}
                className={clsx(
                  classes.imageUploadsDisclaimer,
                  error && classes.errorText,
                )}
              >
                {helperText}
              </Typography>

              {file && (
                <Grow in>
                  <div className={classes.imageContainer}>{renderPreviewImage(file)}</div>
                </Grow>
              )}
            </>
          );
        case 'left':
          return (
            <>
              <Typography variant={'subtitle2'}>
                {renderLabel ? label : ''}
                {required && ' *'}
              </Typography>
              <Grid2 container spacing={1} alignItems={'center'}>
                <Grid2 size={7}>
                  <Grid2 container direction={'column'} spacing={0.5}>
                    <Grid2>
                      {renderUploadTrigger('UPLOAD IMAGE', {
                        idSuffix: 'content-left',
                      })}
                    </Grid2>
                    <Grid2>
                      <Typography variant={'caption'}>
                        {file ? getFileName(file) || 'Image' : 'No Image Uploaded'}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Grid2>
                <Grid2 size={5}>
                  {file && (
                    <div className={classes.imageContainerButtonLeft}>
                      <Grow in>
                        <div className={classes.imageContainer}>
                          {renderPreviewImage(file, {
                            style: {
                              width: '100%',
                              height: 'auto',
                            },
                          })}
                        </div>
                      </Grow>
                      <Tooltip
                        title={'Remove this image'}
                        className={classes.removeImage}
                      >
                        <IconButton
                          size={'small'}
                          onClick={() => {
                            if (resolvedVariant !== 'multiple') {
                              (onRemoveImage as () => void)();
                            }
                          }}
                          aria-label="Remove image"
                        >
                          <IconMDI.Close color={'error'} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </Grid2>
              </Grid2>
            </>
          );
      }
    },
    [
      renderLabel,
      label,
      required,
      file,
      classes,
      error,
      helperText,
      resolvedVariant,
      onRemoveImage,
      renderUploadTrigger,
      renderPreviewImage,
      getFileName,
    ],
  );

  /**
   * Renders the `singleFile` variant: compact row with upload, caption, and optional preview + remove.
   */
  const renderSingleFileVariant = useCallback(() => {
    return (
      <>
        <Grid2 container spacing={1} alignItems={'center'}>
          <Grid2
            size={7}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
          >
            <Grid2 container spacing={0} alignItems={'center'}>
              <Grid2>
                {renderUploadTrigger('UPLOAD IMAGE', {
                  idSuffix: 'single',
                  buttonClassName: classes.uploadButton,
                })}
              </Grid2>
              <Grid2>
                <Typography variant={'caption'}>
                  {file ? getFileName(file) || 'Image' : 'No Image Uploaded'}
                </Typography>
              </Grid2>
            </Grid2>
          </Grid2>
          <Grid2 size={5}>
            {file && (
              <div className={classes.imageContainerButtonLeft}>
                <Grow in>
                  <div className={classes.imageContainer}>{renderPreviewImage(file)}</div>
                </Grow>
                <Tooltip title={'Remove this image'} className={classes.removeImage}>
                  <IconButton
                    size={'small'}
                    onClick={() => {
                      if (resolvedVariant !== 'multiple') {
                        (onRemoveImage as () => void)();
                      }
                    }}
                    aria-label="Remove image"
                  >
                    <IconMDI.Close color={'error'} />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </Grid2>
        </Grid2>
      </>
    );
  }, [
    file,
    classes,
    resolvedVariant,
    onRemoveImage,
    renderUploadTrigger,
    renderPreviewImage,
    getFileName,
  ]);

  /**
   * Renders the multiple file variant with file list and previews.
   */
  const renderMultipleFileVariant = useCallback(() => {
    return (
      <>
        <Typography variant={'subtitle2'}>
          {renderLabel ? label : ''}
          {required && ' *'}
        </Typography>
        <Grid2 container spacing={1} alignItems={'center'}>
          <Grid2>
            {renderUploadTrigger('Choose File(s)', {
              multiple: true,
              idSuffix: 'multiple',
            })}
          </Grid2>
          <Grid2>
            <Typography variant={'caption'} className={classes.fileName}>
              {files && files.length > 0
                ? `${files.length} file(s) selected`
                : 'No files chosen'}
            </Typography>
          </Grid2>
        </Grid2>

        <Typography
          color={'textSecondary'}
          variant={'caption'}
          className={clsx(classes.imageUploadsDisclaimer, error && classes.errorText)}
        >
          {helperText}
        </Typography>

        {files && files.length > 0 && (
          <Grow in>
            <Grid2 container sx={{ px: 2 }}>
              {files.map((selectedFile, index) => (
                <Grid2
                  key={selectedFile.name}
                  size={12}
                  sx={{ py: 2 }}
                  className={classes.imageContainerMultiple}
                >
                  {renderPreviewImage(selectedFile, {
                    className: classes.multiSelectImageContainer,
                  })}
                  <div>
                    <Typography className={classes.fileName} variant={'caption'}>
                      {selectedFile.name}
                    </Typography>
                    <Button
                      variant={'text'}
                      size={'small'}
                      onClick={() => onRemoveImage(index)}
                      aria-label={`Remove file ${selectedFile.name}`}
                    >
                      Remove File
                    </Button>
                  </div>
                </Grid2>
              ))}
            </Grid2>
          </Grow>
        )}
      </>
    );
  }, [
    renderLabel,
    label,
    required,
    files,
    classes,
    error,
    helperText,
    onRemoveImage,
    renderUploadTrigger,
    renderPreviewImage,
  ]);

  /**
   * Renders the banner variant with wide image preview and gradient overlay.
   */
  const renderBannerVariant = useCallback(() => {
    return (
      <>
        <Box className={classes.bannerContainer}>
          <Box position={'absolute'} bottom={0} className={classes.gradientFade} />
          {file && (
            <>
              {renderPreviewImage(file, {
                priority: true,
                className: classes.banner,
                style: { width: '100%', height: '100%', objectFit: 'cover' },
              })}
              {renderRemoveButton()}
            </>
          )}

          {!file && (
            // Native img: imgix URLs are signed per width; next/image rewrites params and warns on CSS sizing.
            <img
              src={AuthImageSrc.src}
              srcSet={AuthImageSrc.srcSet}
              sizes="100vw"
              className={classes.banner}
              alt="Default banner image"
              fetchPriority="high"
            />
          )}
          {renderUploadButton(classes.bannerUploadIconBottomRight)}
        </Box>
        {!hideBannerFooter && (
          <Box display={'flex'} flexDirection={'row'} justifyContent={'center'}>
            <Typography
              color={'textSecondary'}
              variant={'caption'}
              className={clsx(classes.imageUploadsDisclaimer, error && classes.errorText)}
            >
              {helperText}
            </Typography>
          </Box>
        )}
      </>
    );
  }, [
    file,
    classes,
    error,
    helperText,
    hideBannerFooter,
    renderPreviewImage,
    renderRemoveButton,
    renderUploadButton,
  ]);

  /**
   * Interpolates and renders the appropriate variant based on props.
   */
  const interpolateUploaderVariant = useMemo(() => {
    switch (resolvedVariant) {
      case 'avatar':
        return renderAvatarVariant();
      case 'content':
        return renderContentVariant(resolvedButtonPosition);
      case 'banner':
        return renderBannerVariant();
      case 'multiple':
        return renderMultipleFileVariant();
      case 'singleFile':
        return renderSingleFileVariant();
      default:
        // TypeScript should prevent this, but handle gracefully
        return null;
    }
  }, [
    resolvedVariant,
    resolvedButtonPosition,
    renderAvatarVariant,
    renderContentVariant,
    renderBannerVariant,
    renderMultipleFileVariant,
    renderSingleFileVariant,
  ]);

  return (
    <Box data-component="ImageUploader" display="block">
      {interpolateUploaderVariant}
    </Box>
  );
};

export type ImageUploaderVariant =
  | 'content'
  | 'avatar'
  | 'banner'
  | 'multiple'
  | 'singleFile';
export type ButtonPositionVariant = 'left' | 'top';

/**
 * Type for file objects that have a sourceUrl property (e.g., from GraphQL Media type).
 */
export type FileWithSourceUrl = {
  sourceUrl: string;
  path?: string;
  name?: string;
};

/**
 * Type for the onRemoveImage callback.
 * For single file variants, receives no arguments.
 * For multiple variant, receives the file index.
 */
export type OnRemoveImageCallback =
  | ((index?: number) => void)
  | ((index: number) => void);

export type ImageUploaderPropTypes<T extends ImageUploaderVariant> = {
  /** Display variant ('content' | 'avatar' | 'banner' | 'multiple' | 'singleFile') */
  variant?: T;
  /** Whether to display error state */
  error: boolean;
  /** Helper text displayed below the uploader */
  helperText: string;
  /** Props passed to the underlying file input element */
  inputProps: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
  /** Single file (File, Blob, Media object, or object with sourceUrl). Not allowed for 'multiple' variant. */
  file?: T extends 'multiple' ? never : File | Blob | Media | FileWithSourceUrl;
  /** Array of files. Only allowed for 'multiple' variant. */
  files?: T extends 'multiple' ? Array<File> : never;
  /** Callback when image is removed. For 'multiple' variant, receives index. */
  onRemoveImage: T extends 'multiple' ? (index: number) => void : () => void;
  /** Label text displayed above the uploader (default: 'Image') */
  label?: string;
  /** Initial letter for avatar fallback (default: 'E') */
  avatarName?: string;
  /** Whether the uploader is disabled */
  disabled?: boolean;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** Whether to render the label (default: true) */
  renderLabel?: boolean;
  /** Button position for 'content' variant ('left' | 'top', default: 'top') */
  buttonPosition?: T extends 'content' ? ButtonPositionVariant : never;
  /** Storybook only: when true, shows a mock image preview instead of empty state */
  showMockImage?: boolean;
  /** When `variant` is `banner`, omit the caption row below the banner (e.g. profile hero + overlapping avatar). */
  hideBannerFooter?: T extends 'banner' ? boolean : never;
};

export default ImageUploader;
